import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import BuildIcon from "@mui/icons-material/Build";
import CoffeeIcon from "@mui/icons-material/Coffee";
import PetsIcon from "@mui/icons-material/Pets";
import FavoriteIcon from "@mui/icons-material/Favorite";

const StyledDialogTitle = styled(DialogTitle)({
  textAlign: "center",
  paddingBottom: 0,
});

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(3),
}));

const StyledIcon = styled("span")(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.warning.main,
  marginBottom: theme.spacing(2),
}));

const StyledMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

function MaintenanceDialog({ open, onClose, allowClose = false }) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentIconIndex, setCurrentIconIndex] = useState(0);

  const wittyMessages = [
    "maintenance_mode_witty_message_1",
    "maintenance_mode_witty_message_2",
    "maintenance_mode_witty_message_3",
    "maintenance_mode_witty_message_4",
    "maintenance_mode_witty_message_5",
    "maintenance_mode_witty_message_6",
  ];

  const icons = [
    { component: BuildIcon, color: "warning" },
    { component: CoffeeIcon, color: "primary" },
    { component: PetsIcon, color: "secondary" },
    { component: FavoriteIcon, color: "error" },
  ];

  useEffect(() => {
    if (!open) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion) {
      // No animations for users who prefer reduced motion
      return;
    }

    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % wittyMessages.length);
    }, 8000); // Increased from 4000 to 8000ms

    const iconInterval = setInterval(() => {
      setCurrentIconIndex((prev) => (prev + 1) % icons.length);
    }, 4000); // Increased from 2000 to 4000ms

    return () => {
      clearInterval(messageInterval);
      clearInterval(iconInterval);
    };
  }, [open, wittyMessages.length, icons.length]);

  const CurrentIcon = icons[currentIconIndex].component;
  const iconColor = icons[currentIconIndex].color;

  return (
    <Dialog
      open={open}
      onClose={allowClose ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!allowClose}
    >
      <StyledDialogTitle>
        <FormattedMessage
          id="maintenance_mode_title"
          defaultMessage="Maintenance Mode"
        />
      </StyledDialogTitle>
      <StyledDialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <CurrentIcon
              sx={{ fontSize: 64, color: `${iconColor}.main`, mb: 2 }}
            />
            <CircularProgress size={24} sx={{ ml: 1, color: "primary.main" }} />
          </Box>
          <Typography variant="h6" gutterBottom>
            <FormattedMessage
              id="maintenance_mode_heading"
              defaultMessage="System Under Maintenance"
            />
          </Typography>
          <StyledMessage variant="body1" sx={{ minHeight: 80 }}>
            <FormattedMessage
              id={wittyMessages[currentMessageIndex]}
              defaultMessage="We're making things better! Our digital hamsters are spinning their wheels extra fast today."
            />
          </StyledMessage>
          <Alert severity="info" sx={{ mt: 2, width: "100%" }}>
            <FormattedMessage
              id="maintenance_mode_witty_info"
              defaultMessage="Grab a coffee ☕, pet a cat 🐱, or simply take a deep breath. We'll be back before you know it!"
            />
          </Alert>
        </Box>
      </StyledDialogContent>
      {allowClose && (
        <DialogActions>
          <Button onClick={onClose} color="primary">
            <FormattedMessage id="close" defaultMessage="Close" />
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

MaintenanceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  allowClose: PropTypes.bool,
};

export default MaintenanceDialog;
